import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { invalidateDashboardCache } from "./dashboardSlice";
import {
  QuizState,
  QuizStatus,
  MeetingQuizStatusSchema,
  QuizListResponse,
  QuizAttemptSchema,
  QuizAttemptResponse,
} from "@/types/quiz.types";
import * as quizApi from "@/services/api/endpoints/quiz.api";

const initialState: QuizState = {
  meetingId: null,
  status: "not_started",
  questions: [],
  answers: {},
  loading: false,
  error: null,
  isPolling: false,
};

export const generateQuizThunk = createAsyncThunk(
  "quiz/generate",
  async (meetingId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await quizApi.generateQuiz(meetingId);
      dispatch(invalidateDashboardCache());

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to generate quiz");
    }
  }
);

export const checkQuizStatusThunk = createAsyncThunk(
  "quiz/checkStatus",
  async (meetingId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await quizApi.checkQuizStatus(meetingId);
      if (response.status === "completed") {
        dispatch(invalidateDashboardCache());
      }
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to check quiz status");
    }
  }
);

export const fetchQuizQuestionsThunk = createAsyncThunk(
  "quiz/fetchQuestions",
  async (meetingId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await quizApi.getQuizQuestions(meetingId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch quiz questions");
    }
  }
);

export const submitAnswerThunk = createAsyncThunk(
  "quiz/submitAnswer",
  async (data: QuizAttemptSchema, { rejectWithValue }) => {
    try {
      const response = await quizApi.submitQuizAttempt(data);
      return { ...response, selected_alternative: data.selected_alternative, question_id: data.question_id };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to submit answer");
    }
  }
);

const quizSlice = createSlice({
  name: "quiz",
  initialState,
  reducers: {
    resetQuizState: (state, action: PayloadAction<string | undefined>) => {
      state.meetingId = action.payload || null;
      state.status = "not_started";
      state.questions = [];
      state.answers = {};
      state.loading = false;
      state.error = null;
      state.isPolling = false;
    },
    setPolling: (state, action: PayloadAction<boolean>) => {
      state.isPolling = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Generate Quiz
    builder.addCase(generateQuizThunk.pending, (state, action) => {
      state.meetingId = action.meta.arg;
      state.questions = [];
      state.answers = {};
      state.loading = true;
      state.error = null;
      state.status = "processing";
    });
    builder.addCase(generateQuizThunk.fulfilled, (state) => {
      state.loading = false;
      state.status = "processing";
      state.isPolling = true;
    });
    builder.addCase(generateQuizThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.status = "error";
    });

    // Check Status
    builder.addCase(checkQuizStatusThunk.pending, (state, action) => {
      if (state.meetingId !== action.meta.arg) {
        state.meetingId = action.meta.arg;
        state.questions = [];
        state.answers = {};
        state.status = "not_started";
      }
      state.error = null;
      state.loading = true;
    });
    builder.addCase(checkQuizStatusThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.status = action.payload.status;
      if (action.payload.status === "error") {
        state.error = "Failed to generate quiz.";
      }
      if (action.payload.status === "completed" || action.payload.status === "error") {
        state.isPolling = false;
      }
    });
    builder.addCase(checkQuizStatusThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.status = "error";
      state.isPolling = false;
    });

    // Fetch Questions
    builder.addCase(fetchQuizQuestionsThunk.pending, (state, action) => {
      if (state.meetingId !== action.meta.arg) {
        state.meetingId = action.meta.arg;
        state.questions = [];
        state.answers = {};
        state.status = "not_started";
      }
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchQuizQuestionsThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.questions = action.payload.questions;
    });
    builder.addCase(fetchQuizQuestionsThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Submit Answer
    builder.addCase(submitAnswerThunk.fulfilled, (state, action) => {
      const { question_id, ...answerData } = action.payload;
      state.answers[question_id] = answerData;
    });
  },
});

export const { resetQuizState, setPolling } = quizSlice.actions;
export default quizSlice.reducer;
